import { useState, useRef, useEffect, memo } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  MoreVertical,
  Pencil,
  Trash2,
  CheckCircle,
  XCircle,
  Flag,
  UserX,
  Share2,
  CalendarPlus,
} from 'lucide-react';
import ReportModal from '../ReportModal';
import BlockUserConfirmation from '../BlockUserConfirmation';

const PostActionsMenu = ({
  onEdit,
  onDelete,
  onMarkReunited,
  onShare,
  onExtend,
  extendDisabled = false,
  extendDisabledTooltip,
  isReunited = false,
  showReunitedOption = false,
  className = '',
  isOwner = true,
  postUserId,
  postUsername,
  postId,
  postType,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);

  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [showReportModal, setShowReportModal] = useState(false);
  const [showBlockConfirmation, setShowBlockConfirmation] = useState(false);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event) => {
      const isOutsideButton = buttonRef.current && !buttonRef.current.contains(event.target);
      const isOutsideMenu = menuRef.current && !menuRef.current.contains(event.target);
      
      if (isOutsideButton && isOutsideMenu) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    const handleScroll = () => {
      setIsOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    window.addEventListener('scroll', handleScroll, true);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen]);

  const handleToggle = (e) => {
    e.stopPropagation();
    
    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + 4,
        left: rect.right - 176,
      });
    }
    
    setIsOpen(!isOpen);
  };

  const handleEdit = (e) => {
    e.stopPropagation();
    setIsOpen(false);
    onEdit?.();
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    setIsOpen(false);
    onDelete?.();
  };

  const handleMarkReunited = (e) => {
    e.stopPropagation();
    setIsOpen(false);
    onMarkReunited?.();
  };

  const handleShare = (e) => {
    e.stopPropagation();
    setIsOpen(false);
    onShare?.();
  };

  const handleExtend = (e) => {
    e.stopPropagation();
    if (extendDisabled) return;
    setIsOpen(false);
    onExtend?.();
  };

  const handleReport = (e) => {
    e.stopPropagation();
    if (!isAuthenticated) {
      setIsOpen(false);
      navigate('/login');
      return;
    }
    setShowReportModal(true);
    setIsOpen(false);
  };

  const handleBlock = (e) => {
    e.stopPropagation();
    if (!isAuthenticated) {
      setIsOpen(false);
      navigate('/login');
      return;
    }
    setShowBlockConfirmation(true);
    setIsOpen(false);
  };

  return (
    <div className={`relative ${className}`}>
      <button
        ref={buttonRef}
        onClick={handleToggle}
        className="flex items-center justify-center w-8 h-8 rounded-full 
                   hover:bg-gray-100 transition-colors"
        aria-label={t('post_options', 'Post options')}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <MoreVertical className="w-4 h-4 text-muted-foreground" />
      </button>

      {/* Dropdown Menu */}
      {isOpen && createPortal(
        <>
          <div 
            className="fixed inset-0 z-[200]" 
            onClick={() => setIsOpen(false)}
            aria-hidden="true"
          />
          
          {/* Menu */}
          <div 
            ref={menuRef}
            style={{
              position: 'fixed',
              top: menuPosition.top,
              left: Math.max(8, menuPosition.left),
              zIndex: 201,
            }}
            className="bg-white rounded-xl shadow-lg border border-border w-44 overflow-hidden
                       animate-in fade-in-0 zoom-in-95 duration-100"
            role="menu"
            aria-orientation="vertical"
          >
            {onShare && (
              <button
                onClick={handleShare}
                className="w-full flex items-center gap-3 px-4 py-3 text-sm 
                           text-foreground hover:bg-gray-50 transition-colors
                           border-b border-border"
                role="menuitem"
              >
                <Share2 className="w-4 h-4 text-muted-foreground" />
                {t('share', 'Share')}
              </button>
            )}

            {isOwner ? (
              <>
                {/* Edit Option */}
                <button
                  onClick={handleEdit}
                  className="w-full flex items-center gap-3 px-4 py-3 text-sm 
                             text-foreground hover:bg-gray-50 transition-colors"
                  role="menuitem"
                >
                  <Pencil className="w-4 h-4 text-primary" />
                  {t('edit_post', 'Edit Post')}
                </button>

                {/* Extend Option */}
                {onExtend && (
                  <button
                    onClick={handleExtend}
                    disabled={extendDisabled}
                    title={extendDisabled ? extendDisabledTooltip : undefined}
                    className="w-full flex items-center gap-3 px-4 py-3 text-sm 
                               text-foreground hover:bg-gray-50 transition-colors
                               disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                    role="menuitem"
                  >
                    <CalendarPlus className="w-4 h-4 text-primary" />
                    {t('extend_post_button', 'Extend post')}
                  </button>
                )}

                {/* Mark Reunited Option */}
                {showReunitedOption && (
                  <button
                    onClick={handleMarkReunited}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-sm 
                               transition-colors ${
                                 isReunited 
                                   ? 'text-amber-600 hover:bg-amber-50' 
                                   : 'text-green-600 hover:bg-green-50'
                               }`}
                    role="menuitem"
                  >
                    {isReunited ? (
                      <>
                        <XCircle className="w-4 h-4" />
                        {t('undo_reunited', 'Undo Reunited')}
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4" />
                        {t('mark_reunited', 'Mark as Reunited')}
                      </>
                    )}
                  </button>
                )}

                {/* Delete Option */}
                <button
                  onClick={handleDelete}
                  className="w-full flex items-center gap-3 px-4 py-3 text-sm 
                             text-red-600 hover:bg-red-50 transition-colors"
                  role="menuitem"
                >
                  <Trash2 className="w-4 h-4" />
                  {t('delete_post', 'Delete Post')}
                </button>
              </>
            ) : (
              <>
                {/* Report Option */}
                <button
                  onClick={handleReport}
                  className="w-full flex items-center gap-3 px-4 py-3 text-sm 
                             text-foreground hover:bg-gray-50 transition-colors"
                  role="menuitem"
                >
                  <Flag className="w-4 h-4 text-amber-600" />
                  {t('report_post', 'Report Post')}
                </button>

                {/* Block User Option */}
                <button
                  onClick={handleBlock}
                  className="w-full flex items-center gap-3 px-4 py-3 text-sm 
                             text-red-600 hover:bg-red-50 transition-colors"
                  role="menuitem"
                >
                  <UserX className="w-4 h-4" />
                  {t('block_user', 'Block User')}
                </button>
              </>
            )}
          </div>
        </>,
        document.body
      )}

      {!isOwner && (
        <>
          <ReportModal
            isOpen={showReportModal}
            onClose={() => setShowReportModal(false)}
            targetType={postType}
            targetId={postId}
            targetLabel={t('this_post', 'this post')}
          />
          <BlockUserConfirmation
            isOpen={showBlockConfirmation}
            onClose={() => setShowBlockConfirmation(false)}
            username={postUsername || t('this_user', 'this user')}
            userId={postUserId}
          />
        </>
      )}
    </div>
  );
};

export default memo(PostActionsMenu);
